import { useId, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OverlayTrigger, Popover } from "react-bootstrap";
import "../../styles/home.css"; // .job-badge chip styles

// The open claim's Needs-Attention chips, inline in the tab header line
// (Karen 2026-07-25 — the office wants to see what a job needs from any tab).
// SMART OVERFLOW: shows as many chips as fit the space left between the tab
// title and its action buttons; the rest collapse into a "+N" that opens a
// popover listing them. Every chip (shown or overflowed) deep-links to the tab
// where that issue gets fixed. On wide tabs all chips show; on the crowded
// Quotes header they mostly collapse to +N — nothing is lost, it's one click.
//
// Width is measured off a hidden, off-screen copy that always renders ALL chips
// (+ a sample +N), so re-measuring on resize is stable regardless of how many
// are currently visible. A ResizeObserver recomputes when the header resizes.

function Chip({ badge, onNavigate, measure = false }) {
  const cls = `job-badge job-badge-${badge.group}${measure ? "" : " job-badge-link"}`;
  return (
    <button
      {...(measure ? { "data-measure": "" } : {})}
      type="button"
      tabIndex={measure ? -1 : undefined}
      className={cls}
      data-hint={measure ? undefined : `${badge.label} — open the ${badge.tab} tab to sort it out`}
      onClick={measure ? undefined : () => onNavigate(badge)}
    >
      {badge.label}
    </button>
  );
}

export default function TabBadges({ claimId, claimNumber, badges }) {
  const navigate = useNavigate();
  const wrapRef = useRef(null); // visible row — its width is the space available
  const measureRef = useRef(null); // hidden full render used only for widths
  const moreRef = useRef(null);
  const keyboardOpen = useRef(false);
  const popoverId = useId();
  const [showMore, setShowMore] = useState(false);
  const [visible, setVisible] = useState(badges.length);

  const go = (b) => {
    setShowMore(false);
    navigate(`/claims/${claimNumber ?? claimId}?tab=${b.tab}`);
  };
  const closeOnEscape = (event) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    setShowMore(false);
    moreRef.current?.focus();
  };

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const meas = measureRef.current;
    if (!wrap || !meas) return;
    const GAP = 4;
    const recompute = () => {
      const avail = wrap.clientWidth;
      const items = Array.from(meas.querySelectorAll("[data-measure]"));
      const plusEl = meas.querySelector("[data-plus]");
      const plusW = plusEl ? plusEl.getBoundingClientRect().width + GAP : 0;
      let used = 0;
      let count = 0;
      for (let i = 0; i < items.length; i++) {
        const w = items[i].getBoundingClientRect().width + (i > 0 ? GAP : 0);
        // Reserve room for the +N chip unless this is the final badge.
        const reserve = i < items.length - 1 ? plusW : 0;
        if (used + w + reserve <= avail) {
          used += w;
          count += 1;
        } else break;
      }
      setVisible(count);
      if (count === items.length) setShowMore(false);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(wrap);
    // Theme fonts can change chip widths without changing the available space.
    ro.observe(meas);
    return () => ro.disconnect();
  }, [badges]);

  if (!badges.length) return null;
  const shown = badges.slice(0, visible);
  const hidden = badges.slice(visible);

  return (
    <div
      ref={wrapRef}
      className="tab-badges d-flex align-items-center flex-nowrap overflow-hidden position-relative"
      style={{ flex: "1 1 auto", minWidth: 0 }}
    >
      {shown.map((b) => (
        <Chip key={b.key} badge={b} onNavigate={go} />
      ))}
      {hidden.length > 0 ? (
        <OverlayTrigger
          trigger="click"
          show={showMore}
          onToggle={setShowMore}
          onEntered={(node) => {
            if (keyboardOpen.current) node.querySelector("button")?.focus();
            keyboardOpen.current = false;
          }}
          rootClose
          placement="bottom-start"
          overlay={
            <Popover id={popoverId} role="dialog" aria-label="More attention badges" onKeyDown={closeOnEscape}>
              <Popover.Body className="d-flex flex-wrap gap-1 p-2" style={{ maxWidth: 320 }}>
                {hidden.map((b) => (
                  <Chip key={b.key} badge={b} onNavigate={go} />
                ))}
              </Popover.Body>
            </Popover>
          }
        >
          <button
            ref={moreRef}
            type="button"
            className="job-badge job-badge-more text-nowrap"
            data-hint="Show the rest"
            aria-label={`Show ${hidden.length} more attention badges`}
            aria-haspopup="dialog"
            aria-expanded={showMore}
            aria-controls={showMore ? popoverId : undefined}
            onClick={(event) => { keyboardOpen.current = event.detail === 0; }}
            onKeyDown={closeOnEscape}
          >
            +{hidden.length}
          </button>
        </OverlayTrigger>
      ) : null}

      {/* Hidden measuring layer: always renders ALL chips + a sample +N so the
          fit calculation is stable no matter how many are currently visible. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="tab-badges-measure d-flex align-items-center"
        style={{ position: "absolute", left: -99999, top: 0, visibility: "hidden", pointerEvents: "none", whiteSpace: "nowrap" }}
      >
        {badges.map((b) => (
          <Chip key={b.key} badge={b} measure />
        ))}
        <button type="button" tabIndex={-1} data-plus className="job-badge job-badge-more text-nowrap">
          +{badges.length}
        </button>
      </div>
    </div>
  );
}
